<template>
  <div class="tw:mt-6">
    <div class="tw:rounded-xl tw:border tw:border-gray-300 tw:bg-white tw:p-3">
      <div class="tw:flex tw:items-center tw:justify-between tw:py-1">
        <div class="tw:flex tw:items-center">
          <p class="tw:text-xl tw:font-bold">{{ $t('rate_comparison_booking_sites') }}</p>
          <UiTooltip
            theme="customdarkwithpadding"
            tooltip-position="top"
            :label="$t('rate_comparison_booking_sites_info_text')"
            class="tw:cursor-pointer tw:ml-1"
          >
            <InformationOutline class="tw:fill-none tw:stroke-gray-800 tw:h-[22px] tw:w-[22px] tw:ml-1" />
          </UiTooltip>
        </div>
        <div class="tw:flex tw:items-center">
          <span class="tw:bg-blue-200 tw:border tw:border-gray-200 tw:h-[15px] tw:w-[12px] tw:mr-2" />
          <p class="tw:text-sm tw:text-gray-700">{{ $t('lowest_rates_indicator') }}</p>
        </div>
      </div>
      <p class="tw:text-sm tw:text-gray-600 tw:mb-4">{{ $t('ri_filter_subtext') }}</p>

      <div class="tw:mb-4">
        <div v-if="availableDays.length > 1" class="tw:relative tw:flex tw:items-center">
          <button
            v-show="canScrollLeft"
            class="tw:absolute tw:left-0 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-[#DBDDE1] tw:bg-white tw:cursor-pointer tw:p-0 tw:appearance-none tw:leading-none tw:focus:outline-none"
            style="box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15)"
            @click="scrollLeft"
          >
            <svg class="tw:h-3 tw:w-3 tw:rotate-180" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M4 2.5L7.5 6L4 9.5" stroke="#0A1121" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>

          <div
            ref="scrollContainer"
            class="day-scroll tw:flex tw:gap-2 tw:overflow-x-auto tw:scroll-smooth"
            :class="[canScrollLeft ? 'tw:ml-9' : '', canScrollRight ? 'tw:mr-9' : '']"
            :style="scrollMaskStyle"
            @scroll="updateScrollState"
          >
            <button
              v-for="day in availableDays"
              :key="day"
              class="tw:px-3 tw:py-1.5 tw:rounded-full tw:text-sm tw:font-medium tw:border tw:transition-colors tw:whitespace-nowrap tw:flex-shrink-0"
              :class="
                isSelectedDay(day)
                  ? 'tw:bg-blue-200 tw:text-blue-700 tw:border-blue-200'
                  : 'tw:bg-white tw:text-gray-800 tw:border-gray-300 tw:hover:border-blue-700 tw:hover:text-blue-700'
              "
              @click="$emit('select-day', aggregation === 'DAY' ? Number(day) : day)"
            >
              {{ formatDayLabel(day) }}
            </button>
          </div>

          <button
            v-show="canScrollRight"
            class="tw:absolute tw:right-0 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-[#DBDDE1] tw:bg-white tw:cursor-pointer tw:p-0 tw:appearance-none tw:leading-none tw:focus:outline-none"
            style="box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15)"
            @click="scrollRight"
          >
            <svg class="tw:h-3 tw:w-3" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M4 2.5L7.5 6L4 9.5" stroke="#0A1121" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </div>
      <div class="table-wrapper tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white">
        <table
          class="tw:rounded tw:border-0 tw:border-spacing-0 tw:border-separate tw:w-full tw:md:table tw:block tw:md:max-w-full tw:max-w-fit tw:overflow-x-auto tw:whitespace-nowrap"
        >
          <thead class="tw:rounded-t tw:border-0 tw:bg-gray-100">
            <tr class="tw:border-0">
              <th
                class="tw:border tw:border-gray-300 tw:text-sm tw:font-semibold tw:border-t-0 tw:border-l-0 tw:last:border-r-0 tw:text-center tw:min-h-[44px]"
                v-for="channel in columns"
                :key="channel.id"
              >
                <span class="tw:flex tw:items-center tw:justify-center tw:flex-wrap">
                  <img
                    v-if="channel?.id !== 'property'"
                    class="tw:max-w-[20px] tw:max-h-[20px] tw:mr-1.5"
                    :src="`https://imgcy.trivago.com/e_trim/f_auto,dpr_2,bo_1px_solid_transparent,c_pad,h_24,q_auto,w_24${channel?.avatar}`"
                  />
                  {{ channel.id === 'property' ? $t(channel.name) : channel.fullName }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody class="tw:border-0 tw:rounded-b" ref="channels-table">
            <tr v-for="(row, rowIndex) in rows" :key="row.rowId" class="tw:border-0 tw:rounded-b">
              <td
                v-for="(col, i) in columns"
                :key="i"
                class="tw:min-h-[44px] tw:p-[9px] tw:border tw:border-t-0 tw:border-l-0 tw:last:border-r-0"
                :class="[
                  col.id === 'property' ? 'tw:text-left' : 'tw:text-center',
                  { 'tw:bg-[#E7F4FA] tw:text-blue-700 tw:font-bold': row.columns[col.id]?.highlight },
                  rowIndex === rows.length - 1 ? 'tw:border-b-0' : '',
                ]"
              >
                <div
                  v-if="row.columns[col.id]?.color"
                  :class="[col.id === 'property' ? 'tw:justify-start' : 'tw:justify-center', 'tw:flex tw:items-center']"
                >
                  <UiTooltip
                    theme="material"
                    tooltip-position="top"
                    :label="row.columns[col.id].value.length > 32 ? row.columns[col.id].value : ''"
                  >
                    <div
                      :class="[
                        col.id === 'property' ? 'tw:justify-start' : 'tw:justify-center',
                        'tw:flex tw:items-center',
                      ]"
                    >
                      <div
                        class="tw:w-[10px] tw:h-[10px] tw:rounded-full tw:flex tw:mr-1"
                        :style="`background-color: ${row.columns[col.id].color}`"
                      />

                      <span
                        :class="[
                          selectedChannelsArr.length > 4 ? 'tw:max-w-[250px]' : 'tw:max-w-full',
                          row.columns['property']?.id === selectedAccommodationId ? 'tw:font-bold' : '',
                        ]"
                        class="tw:truncate"
                      >
                        {{ row.columns[col.id].value }}
                      </span>
                    </div>
                  </UiTooltip>
                </div>

                <div v-else class="tw:whitespace-nowrap">
                  {{
                    row.columns[col.id]?.value !== 0 && row.columns[col.id]?.value !== null
                      ? row.columns[col.id]?.value
                      : '-'
                  }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, onMounted, nextTick, watch } from 'vue'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'

import { minBy } from '@/utils/Utility.ts'
import { selectedAccommodationId } from '@/layouts/queries'
import { useFormatting } from '@/composables/useFormatting'
import { useSessionValues } from '@/composables/useSessionValues'
const props = defineProps({
  channels: Array,
  competitors: Array,
  filters: Object,
  rates: Object,
  formattedDate: String,
  availableDays: {
    type: Array,
    default: () => [],
  },
  selectedTimestamp: {
    type: [Number, String],
    default: null,
  },
  aggregation: {
    type: String,
    default: 'DAY',
  },
})

defineEmits(['select-day'])

const scrollContainer = ref(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const SCROLL_AMOUNT = 200
const MASK_FADE_SIZE = '28px'

const updateScrollState = () => {
  const el = scrollContainer.value
  if (!el) return
  canScrollLeft.value = el.scrollLeft > 0
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
}

const scrollLeft = () => {
  scrollContainer.value?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' })
}

const scrollRight = () => {
  scrollContainer.value?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' })
}

const scrollMaskStyle = computed(() => {
  if (canScrollLeft.value && canScrollRight.value) {
    return {
      WebkitMaskImage: `linear-gradient(to right, transparent 0, black ${MASK_FADE_SIZE}, black calc(100% - ${MASK_FADE_SIZE}), transparent 100%)`,
      maskImage: `linear-gradient(to right, transparent 0, black ${MASK_FADE_SIZE}, black calc(100% - ${MASK_FADE_SIZE}), transparent 100%)`,
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }
  }

  if (canScrollLeft.value) {
    return {
      WebkitMaskImage: `linear-gradient(to right, transparent 0, black ${MASK_FADE_SIZE}, black 100%)`,
      maskImage: `linear-gradient(to right, transparent 0, black ${MASK_FADE_SIZE}, black 100%)`,
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }
  }

  if (canScrollRight.value) {
    return {
      WebkitMaskImage: `linear-gradient(to right, black 0, black calc(100% - ${MASK_FADE_SIZE}), transparent 100%)`,
      maskImage: `linear-gradient(to right, black 0, black calc(100% - ${MASK_FADE_SIZE}), transparent 100%)`,
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }
  }

  return {}
})

onMounted(() => nextTick(updateScrollState))
watch(
  () => props.availableDays,
  () => nextTick(updateScrollState)
)

const selectedChannelsArr = computed(() => props.filters?.channelIds.split(','))
const { language } = useSessionValues()

const formatDayLabel = timestamp => {
  // For WEEK aggregation the key is "start-end"; show the start date
  const ts = String(timestamp).includes('-') ? String(timestamp).split('-')[0] : timestamp
  return new Date(Number(ts) * 1000).toLocaleDateString(language.value, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

const isSelectedDay = day => {
  if (props.selectedTimestamp == null) return false
  if (props.aggregation === 'DAY') return Number(day) === Number(props.selectedTimestamp)
  return String(day).startsWith(String(props.selectedTimestamp))
}
const { formatCurrency } = useFormatting()
const columns = computed(() => {
  let channelsArr = props.channels.filter(item => selectedChannelsArr.value.includes(item.id.toString()))
  channelsArr = channelsArr.map(channel => {
    return {
      ...channel,
      avatar: getPartnerImage(channel),
    }
  })
  return [{ id: 'property', name: 'hotels', fullName: '' }].concat(channelsArr)
})

const getPartnerImage = channel => {
  if (channel.id == 3250) {
    return '/v1699964222/partnerlogos-squared/3250.svg'
  } else {
    return channel.logo?.localizedFaviconUrlTail !== null
      ? channel.logo?.localizedFaviconUrlTail
      : channel.logo.localizedUrlTail
  }
}

const getChannelsOverviewFromRatesData = allRates => {
  const channelIds = selectedChannelsArr.value
  const breakfast = props.filters.breakfast
  const roomType = props.filters.roomType
  let data = []
  const ret = () => ({
    channels: channelIds,
    data,
  })

  if (!Object.keys(allRates)) {
    return ret()
  }

  // Sorting the competitors for the table: first own hotel, then competitors ordered by name
  const allRatesCopy = JSON.parse(JSON.stringify(allRates))
  const myHotelRates = allRatesCopy[selectedAccommodationId.value]
  delete allRatesCopy[selectedAccommodationId.value]
  const newAllRates = new Map([[selectedAccommodationId.value, myHotelRates], ...Object.entries(allRatesCopy)])
  const allRatesKeys = Array.from(newAllRates.keys())

  const allRatesKeysWithNames = allRatesKeys.map(item => {
    return {
      id: item,
      name: props.competitors.find(competitor => {
        return competitor.id === Number(item)
      })?.name,
    }
  })

  const myPropertyId = allRatesKeysWithNames.shift().id
  const sorted = allRatesKeysWithNames.sort((a, b) => {
    if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1
    }
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1
    }
    return 0
  })

  let allSortedKeys = sorted.map(item => item.id)
  allSortedKeys.unshift(myPropertyId)

  allSortedKeys.map(competitorId => {
    competitorId = parseInt(competitorId)

    if (allRates[competitorId] === undefined) return
    const competitor = allRates[competitorId]
    const competitorChannelRates = []
    channelIds.map(channelId => {
      channelId = parseInt(channelId)

      if (channelIds.indexOf(channelId.toString()) !== -1) {
        const rate = `${roomType}${breakfast === 'included' ? 'Breakfast' : ''}`
        competitorChannelRates.push({
          colName: channelId,
          value: competitor[channelId] ? Math.round(competitor[channelId][rate] / 100) : null,
        })
      }
    })

    const competitorData = [competitorId].concat(competitorChannelRates)
    data.push(competitorData)
  })

  return ret()
}

const rows = computed(() => {
  const { channels, data } = getChannelsOverviewFromRatesData(props.rates)

  data.map((row, index) => {
    const competitorId = data[index][0]
    const competitor = props.competitors.find(item => item.id === competitorId)
    data[index][0] = {
      colName: 'property',
      id: competitorId,
      index: props.competitors.indexOf(competitor) + 1,
      color: competitor.color,
      value: competitor.name,
    }
  })

  channels.map((channelId, index) => {
    const ratesOfChannel = []
    data.map((row, rowIndex) => {
      if (row && row.length) {
        row.map((column, columnIndex) => {
          if (columnIndex === index + 1) {
            ratesOfChannel.push({
              colName: column?.colName,
              value: column?.value !== null && column?.value !== 0 ? column?.value : null,
              rowIndex,
              columnIndex,
            })
          }
        })
      }
    })
    const rateValue = rate => rate.value
    const { rowIndex, columnIndex, value } = minBy(ratesOfChannel, rateValue) || {}

    if (data[rowIndex] && data[rowIndex][columnIndex]) {
      ratesOfChannel.forEach(item => {
        if (item.value === value) {
          data[item.rowIndex][item.columnIndex] = {
            colName: data[rowIndex][columnIndex].colName,
            value: data[rowIndex][columnIndex].value,
            rate: true,
            highlight: true,
          }
        }
      })
    }

    const channelsLength = channels.length
    data.map((items, index) => {
      const fillMissingValuesCount = channelsLength - items.slice(1, items.length).length
      if (fillMissingValuesCount > 0) {
        for (let i = 0; i < fillMissingValuesCount; i++) {
          data[index].push(null)
        }
      }
    })
  })
  const table = []
  data.forEach((row, rowIndex) => {
    const columns = {}
    row.forEach(cell => {
      columns[cell.colName] = {
        ...cell,
        value:
          cell.value === null || cell.value === 0
            ? 0
            : typeof cell.value === 'number'
              ? formatCurrency(cell.value, {
                  divideBy100: false,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })
              : cell.value,
      }
    })
    table.push({ rowId: +rowIndex, columns })
  })
  return table
})
</script>
<style scoped>
.day-scroll {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE / Edge */
}
.day-scroll::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
.table-wrapper {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  overflow: auto;
  white-space: nowrap;
}
</style>
